# Job categories

The [`jobCategories` query] returns a superset of job categories supported across SEEK’s markets by default.
However, presenting this entire hierarchy for selection is suboptimal:

1. The hirer may see and be confused by overlapping categories.

   While our hierarchy aims to be [mutually exclusive and collectively exhaustive][mece] where possible,
   this principle cannot hold at a global level when we account for regional differences such as governmental structures.
   For example, Australia has a `Government - Local` entry and New Zealand has a distinct `Local Government` entry.

2. The performance of the job ad will be adversely impacted if the selected job category is not relevant to the region,
   as it will be less likely to appear in recommendations and search results.

[`jobcategories` query]: /schema/query/jobCategories
[mece]: https://en.wikipedia.org/wiki/MECE_principle

This issue will be exacerbated as we continue to evolve our ontologies for regional needs across the Asia Pacific.
To ensure that hirers are only presented with relevant categories,
provide the [`positionProfile`] context when retrieving job categories:

[`positionProfile`]: /schema/query/jobCategories#/argument/positionProfile

```graphql
query (
  $positionProfile: JobCategories_PositionProfileInput!
  $schemeId: String!
) {
  jobCategories(
    # Request results to be tailored to the position’s location.
    positionProfile: $positionProfile
    schemeId: $schemeId
  ) {
    id {
      value
    }
    name
    children {
      id {
        value
      }
      name
    }
  }
}
```

```json
{
  "positionProfile": {
    "positionLocation": "seekAnzPublicTest:location:seek:2dkY4vZJF"
  },
  "schemeId": "seekAnzPublicTest"
}
```

```jsonc lovato-ignore-runnable-graphql-operation
{
  "jobCategories": [
    // ...
    {
      "id": {
        "value": "seekAnzPublicTest:jobCategory:seek:2bdZVXomZ"
      },
      "name": "Government & Defence",
      "children": [
        // ...
        {
          "id": {
            "value": "seekAnzPublicTest:jobCategory:seek:27FP8ebpT"
          },
          "name": "Government - Federal"
        },
        // Results are tailored to Australia and exclude overlapping entries
        // that exist in other regions like `Local Government` in New Zealand.
        {
          "id": {
            "value": "seekAnzPublicTest:jobCategory:seek:27kGJj6M5"
          },
          "name": "Government - Local"
        },
        {
          "id": {
            "value": "seekAnzPublicTest:jobCategory:seek:28F9Uoash"
          },
          "name": "Government - State"
        }
      ]
    }
  ]
}
```
